import React, { useState, useEffect } from 'react';
import whatsappService from '../../services/whatsapp';
import notificationLogger from '../../services/notificationLogger';
import invoiceGenerator from '../../services/invoiceGenerator';
import './WhatsAppIntegration.css';

export default function WhatsAppIntegration({
  saleData,
  customerData,
  onClose,
  autoSend = false
}) {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);
  const [messagePreview, setMessagePreview] = useState('');

  useEffect(() => {
    generatePreview();
    if (autoSend) {
      handleSendNotification();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generatePreview = () => {
    // Use the invoiceUrl from saleData if available, otherwise construct it with production URL
    const invoiceUrl = saleData.invoiceUrl || `https://et-pos.store/invoice/${saleData.invoiceHash || saleData.id}`;
    const preview = `
Hi ${customerData.name},

Thank you for your purchase!

Invoice #: ${saleData.invoiceNumber}
Amount: $${parseFloat(saleData.total || 0).toFixed(2)}

You can view your invoice here: ${invoiceUrl}

Thank you for your business!
- ${saleData.businessName || "Lefty's Liquor"}
    `.trim();

    setMessagePreview(preview);
  };

  const handleSendNotification = async () => {
    if (!customerData.phone) {
      setError('Customer phone number is required');
      return;
    }

    setSending(true);
    setError(null);

    try {
      // Use the invoiceUrl from saleData, or generate it using invoiceHash or id with production URL
      const invoiceUrl = saleData.invoiceUrl || 
                        (saleData.invoiceHash ? `https://et-pos.store/invoice/${saleData.invoiceHash}` : 
                        invoiceGenerator.generateShareableLink(saleData.id));

      // Prepare invoice data for WhatsApp — all 6 template variables
      const whatsappData = {
        customerName:  String(customerData.name || ''),
        customerPhone: customerData.phone,
        invoiceNumber: String(saleData.invoiceNumber || ''),
        invoiceDate:   String(saleData.date || new Date().toLocaleDateString()),
        totalAmount:   String(parseFloat(saleData.total || 0).toFixed(2)),
        paymentStatus: String(saleData.paymentStatus || 'Due'),
        invoiceUrl:    String(invoiceUrl || ''),
      };

      // Send WhatsApp notification
      const response = await whatsappService.sendInvoiceNotification(whatsappData);

      // Log the notification
      await notificationLogger.logNotification({
        type: 'invoice',
        recipientPhone: customerData.phone,
        recipientName: customerData.name,
        status: 'sent',
        provider: whatsappService.provider,
        templateName: 'invoice',
        messageId: response.messages?.[0]?.id || response.messageId || 'unknown',
        errorMessage: null,
        metadata: {
          invoice_number: saleData.invoiceNumber,
          sale_id: saleData.id,
          amount: saleData.total
        }
      });

      setSent(true);

      // Auto close after 2 seconds if successful
      setTimeout(() => {
        if (onClose) onClose();
      }, 2000);

    } catch (err) {
      console.error('WhatsApp notification error:', err);
      setError(err.message || 'Failed to send WhatsApp notification');

      // Log the failed notification
      await notificationLogger.logNotification({
        type: 'invoice',
        recipientPhone: customerData.phone,
        recipientName: customerData.name,
        status: 'failed',
        provider: whatsappService.provider,
        templateName: 'invoice',
        messageId: null,
        errorMessage: err.message,
        metadata: {
          invoice_number: saleData.invoiceNumber,
          sale_id: saleData.id,
          amount: saleData.total
        }
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="whatsapp-notification-modal">
      <div className="whatsapp-modal-content">
        <div className="whatsapp-modal-header">
          <h3>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
            </svg>
            Send WhatsApp Invoice
          </h3>
          {onClose && (
            <button className="close-btn" onClick={onClose}>×</button>
          )}
        </div>

        <div className="whatsapp-modal-body">
          <div className="customer-info">
            <h4>Customer Details</h4>
            <p><strong>Name:</strong> {customerData.name}</p>
            <p><strong>Phone:</strong> {customerData.phone || 'Not provided'}</p>
            {customerData.email && <p><strong>Email:</strong> {customerData.email}</p>}
          </div>

          <div className="invoice-info">
            <h4>Invoice Details</h4>
            <p><strong>Invoice #:</strong> {saleData.invoiceNumber}</p>
            <p><strong>Amount:</strong> ${parseFloat(saleData.total || 0).toFixed(2)}</p>
            <p><strong>Date:</strong> {saleData.date || new Date().toLocaleDateString()}</p>
          </div>

          <div className="message-preview">
            <h4>Message Preview</h4>
            <div className="preview-box">
              <pre>{messagePreview}</pre>
            </div>
            <p className="preview-note">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
              This message will be sent using an approved WhatsApp Business template
            </p>
          </div>

          {error && (
            <div className="error-message">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="15" y1="9" x2="9" y2="15"></line>
                <line x1="9" y1="9" x2="15" y2="15"></line>
              </svg>
              {error}
            </div>
          )}

          {sent && (
            <div className="success-message">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
              WhatsApp notification sent successfully!
            </div>
          )}
        </div>

        <div className="whatsapp-modal-footer">
          {onClose && (
            <button
              className="btn-secondary"
              onClick={onClose}
              disabled={sending}
            >
              Cancel
            </button>
          )}
          <button
            className="btn-whatsapp"
            onClick={handleSendNotification}
            disabled={sending || sent || !customerData.phone}
          >
            {sending ? (
              <>
                <span className="spinner"></span>
                Sending...
              </>
            ) : sent ? (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                Sent
              </>
            ) : (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
                </svg>
                Send WhatsApp
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
